import { useEffect, useState } from "react";
import type { AccountRecord } from "@/types";
import { CallLink } from "@/components/calling/CallLink";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/utils";

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3.5 py-3">
    <h2 className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
    {children}
  </section>
);
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col border-b border-border/60 py-1.5 last:border-b-0">
    <span className="text-[11.5px] text-muted-foreground">{label}</span>
    <span className="break-words text-[13px] font-medium">{children}</span>
  </div>
);
const link = "text-primary hover:underline";

interface CreditPosition { limit: number | null; open_orders_value: number; open_orders: number; available: number | null }

/** The customer's usual payment terms, and what they owe in open orders against their credit limit */
function PaymentAndCredit({ account }: { account: AccountRecord }) {
  const { hasPermission } = useAuth();
  const [termName, setTermName] = useState<string | null>(null);
  const [credit, setCredit] = useState<CreditPosition | null>(null);
  const [listName, setListName] = useState<string | null>(null);
  useEffect(() => {
    api.get("/api/PaymentTerm/List").then((r) => {
      const terms = unwrapArray<{ id: string; label: string; is_default: boolean }>(r);
      const t = terms.find((x) => x.id === account.payment_term_id);
      setTermName(t ? t.label : terms.find((x) => x.is_default) ? `${terms.find((x) => x.is_default)!.label} (company default)` : null);
    }).catch(() => setTermName(null));
    if (account.price_list_id) api.get("/api/PriceList/List").then((r) => setListName(unwrapArray<{ id: string; name: string }>(r).find((p) => p.id === account.price_list_id)?.name || "A price list that is switched off")).catch(() => setListName(null));
    else setListName(null);
    if (hasPermission("order.read")) api.get(`/api/SalesOrder/Credit/${account.id}`).then((r) => setCredit(unwrapObject<CreditPosition>(r))).catch(() => setCredit(null));
  }, [account.id, account.payment_term_id, account.price_list_id]);
  const hasLimit = account.credit_limit !== null && account.credit_limit !== undefined && account.credit_limit !== "";
  if (!termName && !hasLimit && !listName && !(credit && credit.open_orders > 0)) return null;
  return (
    <Card title="Payment & credit">
      {termName && <Row label="Usual payment terms">{termName}</Row>}
      {listName && <Row label="Price list">{listName}</Row>}
      <Row label="Credit limit">{hasLimit ? formatINR(account.credit_limit as number) : "No limit"}</Row>
      {credit && (
        <Row label="Open orders (not yet paid)">
          <span className={hasLimit && credit.limit !== null && credit.open_orders_value > credit.limit ? "text-destructive" : ""}>
            {formatINR(credit.open_orders_value)}
            {credit.available !== null && <span className="ml-1.5 text-xs font-normal text-muted-foreground">· {formatINR(credit.available)} left</span>}
          </span>
        </Row>
      )}
    </Card>
  );
}

/** The account's details: company, addresses, tax and notes. Empty fields are left out. */
export function AccountProperties({ account }: { account: AccountRecord }) {
  const company: [string, React.ReactNode][] = [
    ["Industry", account.industry],
    ["Phone", account.phone && <CallLink type="ACCOUNT" id={account.id} field="phone" number={account.phone} className={link}>{account.phone}</CallLink>],
    ["Corporate email", account.email && <a href={`mailto:${account.email}`} className={link}>{account.email}</a>],
    ["Website", account.website && <a href={/^https?:\/\//.test(account.website) ? account.website : `https://${account.website}`} target="_blank" rel="noreferrer" className={link}>{account.website.replace(/^https?:\/\//, "")}</a>],
  ];
  const tax: [string, React.ReactNode][] = [
    ["GSTIN", account.gstin && <span className="font-mono text-xs">{account.gstin}</span>],
    ["State", account.state],
  ];
  const shown = (rows: [string, React.ReactNode][]) => rows.filter(([, v]) => v);
  const cards: [string, [string, React.ReactNode][]][] = [
    ["Company", shown(company)],
    ["Tax & region", shown(tax)],
  ];

  return (
    <div className="flex flex-col gap-3">
      {cards.map(([title, rows]) =>
        rows.length > 0 ? (
          <Card key={title} title={title}>
            {rows.map(([label, value]) => (
              <Row key={label} label={label}>
                {value}
              </Row>
            ))}
          </Card>
        ) : null,
      )}
      <PaymentAndCredit account={account} />
      {(account.billing_address || account.shipping_address) && (
        <Card title="Addresses">
          {account.billing_address && (
            <Row label="Billing">
              <span className="whitespace-pre-line">{account.billing_address}</span>
            </Row>
          )}
          {account.shipping_address && (
            <Row label="Delivery">
              <span className="whitespace-pre-line">{account.shipping_address}</span>
            </Row>
          )}
        </Card>
      )}
      {account.notes && (
        <Card title="Notes">
          <p className="whitespace-pre-line text-[13px]">{account.notes}</p>
        </Card>
      )}
    </div>
  );
}
